/** Pure helpers for the pem-ca-bundle certificate editor. */

import { isAxiosError } from 'axios';

const END = '-----END CERTIFICATE-----';

/** Split a PEM bundle into its certificate blocks (each ends with a newline). */
export function splitPemBlocks(pem: string): string[] {
  return pem
    .split(END)
    .map((chunk, i, all) => (i < all.length - 1 ? chunk : ''))
    .map((chunk) => chunk.trim())
    .filter((chunk) => chunk.includes('-----BEGIN CERTIFICATE-----'))
    .map((chunk) => `${chunk.slice(chunk.indexOf('-----BEGIN CERTIFICATE-----'))}\n${END}\n`);
}

/** Drop block `index`; null when the bundle does not hold `expected` blocks. */
export function removePemBlock(pem: string, index: number, expected: number): string | null {
  const blocks = splitPemBlocks(pem);
  if (blocks.length !== expected || index < 0 || index >= blocks.length) return null;
  return blocks.filter((_, i) => i !== index).join('');
}

export function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(bin);
}

export function readFileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => { resolve(bytesToBase64(new Uint8Array(reader.result as ArrayBuffer))); };
    reader.onerror = () => { reject(new Error('Could not read the file.')); };
    reader.readAsArrayBuffer(file);
  });
}

export function utf8ToBase64(text: string): string {
  return bytesToBase64(new TextEncoder().encode(text));
}

/** User-facing message for a failed parse-ca call. */
export function describeCaUploadError(error: unknown): string {
  if (isAxiosError(error)) {
    const detail: unknown = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    if (detail && typeof detail === 'object') {
      const { code, message } = detail as { code?: string; message?: string };
      if (code === 'bad_password') {
        return 'The PFX password is incorrect (or the file is not password-protected). Check it and try again.';
      }
      if (message) return message;
    }
    if (typeof detail === 'string') return detail;
  }
  return error instanceof Error ? error.message : 'Could not read certificates from that input.';
}

export function summarizeCaUpload(r: {
  added: number;
  already_present: number;
  skipped_not_ca: string[];
  skipped_expired: string[];
}): string {
  const parts = [`${String(r.added)} added`, `${String(r.already_present)} already present`];
  if (r.skipped_not_ca.length) parts.push(`${String(r.skipped_not_ca.length)} skipped (not a CA)`);
  if (r.skipped_expired.length) parts.push(`${String(r.skipped_expired.length)} skipped (expired)`);
  return parts.join(', ');
}
